import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Search, Eye, Send, CheckCircle, Clock, CalendarDays, RefreshCw, Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (v == null) return '-';
  return Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 });
};

const PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "q1", label: "Q1 (Apr-Jun)" },
  { value: "q2", label: "Q2 (Jul-Sep)" },
  { value: "q3", label: "Q3 (Oct-Dec)" },
  { value: "q4", label: "Q4 (Jan-Mar)" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom Date" },
];

// Returns {start, end} (inclusive) for a period, or null for all time.
// Quarters and years follow the Indian financial year (Apr to Mar).
const getPeriodRange = (period, customFrom, customTo) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const fy = m >= 3 ? y : y - 1; // financial year start year
  const endOf = (yy, mm) => new Date(yy, mm + 1, 0, 23, 59, 59, 999);
  switch (period) {
    case "this_month": return { start: new Date(y, m, 1), end: endOf(y, m) };
    case "last_month": return { start: new Date(y, m - 1, 1), end: endOf(y, m - 1) };
    case "q1": return { start: new Date(fy, 3, 1), end: endOf(fy, 5) };
    case "q2": return { start: new Date(fy, 6, 1), end: endOf(fy, 8) };
    case "q3": return { start: new Date(fy, 9, 1), end: endOf(fy, 11) };
    case "q4": return { start: new Date(fy + 1, 0, 1), end: endOf(fy + 1, 2) };
    case "this_year": return { start: new Date(fy, 3, 1), end: endOf(fy + 1, 2) };
    case "last_year": return { start: new Date(fy - 1, 3, 1), end: endOf(fy, 2) };
    case "custom": {
      if (!customFrom && !customTo) return null;
      const start = customFrom ? new Date(`${customFrom}T00:00:00`) : new Date(0);
      const end = customTo ? new Date(`${customTo}T23:59:59.999`) : new Date(8640000000000000);
      return { start, end };
    }
    default: return null;
  }
};

const parseInvoiceDate = (dateStr) => {
  if (!dateStr) return null;
  // Invoice dates are stored as dd/mm/yyyy. new Date() would read these as mm/dd/yyyy.
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(dateStr).trim());
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  // Also handles "06 Jun 2026", "2026-03-17", etc.
  const d = new Date(dateStr);
  if (!isNaN(d)) return d;
  return null;
};

const AdminInvoices = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [period, setPeriod] = useState("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const isAdmin = user?.role === 'admin';
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [zipping, setZipping] = useState(false);

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data);
    } catch (err) {
      toast.error("Failed to load invoices");
    } finally { setLoading(false); }
  };

  const handleDownload = async (inv) => {
    try {
      const res = await axios.get(`${API}/bookings/${inv.booking_id}/invoice/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${inv.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download invoice");
    }
  };

  const handleSendToAgent = async (inv) => {
    try {
      await axios.put(`${API}/bookings/${inv.booking_id}/invoice/send-to-agent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice sent to agent!");
      fetchInvoices();
    } catch (err) {
      toast.error("Failed to send invoice");
    }
  };

  const handleBulkDownload = async () => {
    setZipping(true);
    try {
      const res = await axios.post(`${API}/invoices/bulk-pdf-zip`,
        { booking_ids: [...selectedIds] },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Invoices_${new Date().toISOString().slice(0, 10)}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selectedIds.size} invoice${selectedIds.size > 1 ? 's' : ''} downloaded as ZIP`);
      setSelectedIds(new Set());
    } catch { toast.error("Failed to create ZIP"); }
    finally { setZipping(false); }
  };

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.filter(i => i.booking_id).map(i => i.booking_id)));
    else setSelectedIds(new Set());
  };

  const toggleOne = (bookingId, checked) => {
    const next = new Set(selectedIds);
    checked ? next.add(bookingId) : next.delete(bookingId);
    setSelectedIds(next);
  };

  const filtered = invoices.filter(inv => {
    const matchSearch = search === "" ||
      (inv.invoice_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.booking_destination || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.agent_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.guest_name || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" ||
      (filter === "sent" && inv.sent_to_agent) ||
      (filter === "unsent" && !inv.sent_to_agent);
    // Period filter
    let matchMonth = true;
    const range = getPeriodRange(period, customFrom, customTo);
    if (range) {
      const invDate = parseInvoiceDate(inv.invoice_date);
      if (invDate) matchMonth = invDate >= range.start && invDate <= range.end;
    }
    return matchSearch && matchFilter && matchMonth;
  });

  const totalAmount = filtered.reduce((s, inv) => s + (inv.total_amount || inv.grand_total || 0), 0);
  const totalGst = filtered.reduce((s, inv) => s + (inv.tax_amount || 0), 0);
  const sentCount = filtered.filter(i => i.sent_to_agent).length;

  return (
    <DashboardLayout type={isAdmin ? "admin" : "agent"}>
      <div className="space-y-6" data-testid="invoices-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            <p className="text-gray-500 text-sm">Manage and track all invoices</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="renumber-invoices-btn"
                onClick={async () => {
                  if (!window.confirm("Renumber ALL invoices into clean sequential format HH-INV-{FY}-0001 onwards? Safe to run multiple times.")) return;
                  try {
                    const res = await axios.post(
                      `${API}/admin/invoices/renumber-sequential?dry_run=false`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const n = res.data?.renumbered ?? 0;
                    toast.success(n > 0 ? `Renumbered ${n} invoice(s)` : "All invoices already in sequence");
                    fetchInvoices();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to renumber invoices");
                  }
                }}
                className="gap-2 border-gray-200 text-gray-700"
              >
                <Hash className="w-4 h-4" /> Renumber 0001+
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="recompute-invoices-btn"
                onClick={async () => {
                  if (!window.confirm("Recompute totals for all invoices using DMC base price (no agent markup)? This is safe to run repeatedly.")) return;
                  try {
                    const res = await axios.post(
                      `${API}/admin/invoices/backfill-new-math?dry_run=false`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const n = res.data?.updated ?? res.data?.would_update ?? 0;
                    toast.success(`Recomputed ${n} invoice(s) using DMC base price`);
                    fetchInvoices();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to recompute invoices");
                  }
                }}
                className="gap-2 border-gray-200 text-gray-700"
              >
                <RefreshCw className="w-4 h-4" /> Recompute Totals
              </Button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Invoices</p>
                <p className="text-xl font-bold text-gray-900" data-testid="total-invoices-count">{filtered.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Sent to Agent</p>
                <p className="text-xl font-bold text-gray-900" data-testid="sent-invoices-count">{sentCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <Hash className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total GST Amt</p>
                <p className="text-xl font-bold text-gray-900" data-testid="total-invoices-gst">{fmt(totalGst)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Invoice Amt</p>
                <p className="text-xl font-bold text-gray-900" data-testid="total-invoices-amount">{fmt(totalAmount)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filter — single row */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
              {/* Search — takes all remaining space */}
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by invoice #, destination, agent, guest..."
                  className="pl-9 bg-gray-50 border-gray-200 w-full"
                  data-testid="invoice-search-input"
                />
              </div>
              {/* Sent/Unsent dropdown — admin only */}
              {isAdmin && (
                <select
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className="h-9 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 cursor-pointer shrink-0 w-28"
                  data-testid="invoice-status-filter"
                >
                  <option value="all">All</option>
                  <option value="sent">Sent</option>
                  <option value="unsent">Unsent</option>
                </select>
              )}
              {/* Period dropdown */}
              <div className="relative shrink-0">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <select
                  value={period}
                  onChange={e => setPeriod(e.target.value)}
                  className="h-9 rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 appearance-none cursor-pointer w-40"
                  data-testid="period-select"
                >
                  {PERIOD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {/* Custom date range — only when Period = Custom Date */}
              {period === "custom" && (
                <>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={e => setCustomFrom(e.target.value)}
                    className="h-9 w-36 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 shrink-0"
                    data-testid="period-custom-from"
                  />
                  <input
                    type="date"
                    value={customTo}
                    onChange={e => setCustomTo(e.target.value)}
                    className="h-9 w-36 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 shrink-0"
                    data-testid="period-custom-to"
                  />
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoice Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Batch actions bar */}
                {isAdmin && selectedIds.size > 0 && (
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-violet-50 border-b border-violet-100">
                    <span className="text-sm text-violet-700 font-medium">{selectedIds.size} selected</span>
                    <Button size="sm" onClick={handleBulkDownload} disabled={zipping} className="gap-1.5 h-7 bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid="bulk-download-zip-btn">
                      {zipping ? <><Loader2 className="w-3 h-3 animate-spin" /> Preparing...</> : <><Download className="w-3 h-3" /> Download ZIP</>}
                    </Button>
                    <button onClick={() => setSelectedIds(new Set())} className="text-xs text-violet-500 hover:text-violet-700">Clear</button>
                  </div>
                )}
                <table className="w-full" data-testid="invoices-table">
                  <thead className="bg-gray-50/80">
                    <tr>
                      {isAdmin && (
                        <th className="px-4 py-3 w-8">
                          <input
                            type="checkbox"
                            checked={selectedIds.size === filtered.filter(i => i.booking_id).length && filtered.length > 0}
                            onChange={e => toggleAll(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-violet-600 cursor-pointer accent-violet-600"
                            data-testid="select-all-invoices"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Invoice #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Destination</th>
                      {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Agent</th>}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Guest</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Amount</th>
                      {isAdmin && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Status</th>}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map(inv => (
                      <tr key={inv.id} className={`hover:bg-gray-50/50 transition-colors ${selectedIds.has(inv.booking_id) ? 'bg-violet-50/40' : ''}`} data-testid={`invoice-row-${inv.id}`}>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(inv.booking_id)}
                              onChange={e => toggleOne(inv.booking_id, e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-violet-600"
                              data-testid={`select-invoice-${inv.id}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm font-semibold text-violet-700">{inv.invoice_number}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{inv.invoice_date}</td>
                        <td className="px-4 py-3 text-sm text-gray-800 font-medium">{inv.booking_destination || inv.trip_details?.destination || '-'}</td>
                        {isAdmin && <td className="px-4 py-3 text-sm text-gray-600">{inv.agent_name || inv.bill_to?.company || '-'}</td>}
                        <td className="px-4 py-3 text-sm text-gray-600">{inv.guest_name || inv.trip_details?.guest_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{fmt(inv.total_amount || inv.grand_total)}</td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-center">
                            {inv.sent_to_agent ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Sent</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Draft</Badge>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/${isAdmin ? 'admin' : 'agent'}/bookings/${inv.booking_id}`)}
                              title="View Booking"
                              data-testid={`view-booking-${inv.id}`}
                            >
                              <Eye className="w-4 h-4 text-gray-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(inv)}
                              title="Download PDF"
                              data-testid={`download-invoice-${inv.id}`}
                            >
                              <Download className="w-4 h-4 text-violet-600" />
                            </Button>
                            {isAdmin && !inv.sent_to_agent && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendToAgent(inv)}
                                title="Send to Agent"
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`send-invoice-${inv.id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminInvoices;
